import PlusCircle from 'assets/icons/PlusCircle';
import { ROUTINE, SETTING_TAB_INDEX } from 'constants/general';
import { t } from 'services/i18n.service';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { addHabitButtonSelector } from 'store/reducer/setting/selectors';
import {
  newHabit,
  updateIsNewCustomRoutineAppended
} from 'store/reducer/setting/slice';
import { isMobilePlatform } from 'utils/settings.util';
import { decrement, getNewHabitProps } from 'utils/helpers';
import { cloneDeep } from 'utils/helpers/cloneDeep';
import colors from 'constants/color';
import BaseButton, { ButtonSize } from 'components/ui/BaseButton';

interface AddHabitButtonProps {
  routine?: string;
  title?: string;
  customRoutinePosition?: number;
  additionalBtnStyles?: string;
  size?: ButtonSize;
}

const AddHabitButton = ({
  routine,
  title,
  customRoutinePosition,
  additionalBtnStyles,
  size = 'sm'
}: AddHabitButtonProps) => {
  const dispatch = useAppDispatch();
  const {
    tabs,
    currentSettings,
    activeTabIndex,
    customRoutine,
    routines,
    platform
  } = useAppSelectorShallow(addHabitButtonSelector);

  const isCustomRoutinesTab = useMemo(
    () =>
      isMobilePlatform(platform)
        ? activeTabIndex === decrement(SETTING_TAB_INDEX.CUSTOM_ROUTINES)
        : activeTabIndex === SETTING_TAB_INDEX.CUSTOM_ROUTINES,
    [activeTabIndex, platform]
  );

  const [activity_type, sequence_id] = useMemo(
    () =>
      getNewHabitProps(
        tabs,
        activeTabIndex,
        cloneDeep(currentSettings),
        platform,
        customRoutine,
        routines,
        isCustomRoutinesTab,
        customRoutinePosition
      ),
    [
      tabs,
      activeTabIndex,
      currentSettings,
      customRoutine,
      routines,
      customRoutinePosition,
      isCustomRoutinesTab,
      platform
    ]
  );

  return (
    <div
      className={`w-fit h-fit animate-fadeIn ${routine ? 'flex flex-col items-center justify-center ~gap-4/6 ~p-0/10 ~my-4/16' : 'self-center'}`}>
      {routine ? (
        <h5 className='italic text-center text-black dark:text-secondary-dark-900'>
          {t(
            routine === ROUTINE.CUSTOM_ROUTINES
              ? 'make_at_least_one_new_custom_routine_to_your_custom_routines'
              : 'make_at_least_one_new_habit_a_part_of_your_routine',
            {
              routine: routine || t('custom_routine')
            }
          )}
        </h5>
      ) : null}
      <BaseButton
        onClick={() => {
          dispatch(
            newHabit({
              type: activity_type,
              sequence_id,
              customRoutinePosition
            })
          );
          customRoutinePosition === undefined &&
            dispatch(updateIsNewCustomRoutineAppended(true));
        }}
        icon={
          <PlusCircle
            styles='min-w-3 min-h-3 ~w-3/4 ~h-3/4 rounded-full cursor-pointer'
            fill={colors.PRIMARY.LIGHT_500}
          />
        }
        title={
          routine
            ? t(
                routine === ROUTINE.CUSTOM_ROUTINES
                  ? 'add_your_first_custom_routine'
                  : 'add_your_first_routine_habit',
                {
                  routine:
                    routine === ROUTINE.CUSTOM_ROUTINES
                      ? t('custom_routine')
                      : routine
                }
              )
            : (title ?? t('add_a_habit'))
        }
        additionalStyles={additionalBtnStyles}
        size={size}
        variant='secondary'
      />
    </div>
  );
};

export default AddHabitButton;
